// actionLogRedact — PII-scrubber för action-log-events.
//
// Policy: endast ids, kind, och numeriska skalärer passerar. All user-typed
// text (block-names, beskrivningar, filnamn) strippas eller hashas. Strings
// som ser ut som emails, JWT:s, api-keys, urls med query-params redacteras
// till `[redacted:<kind>]`.

import type { ActionKind } from '@/lib/actionLogKinds';
import { isActionKind } from '@/lib/actionLogKinds';

export interface RawActionEvent {
  kind: string;
  target?: string;
  /** Godtycklig raw payload — kommer från call-sites, kan innehålla PII. */
  payload?: Record<string, unknown>;
}

export interface RedactedActionEntry {
  kind: ActionKind;
  target?: string;
  /** Bara numeriska skalärer + ids som passerat allowlist. */
  deltaScalars?: Record<string, number>;
  /** Ids (block/connector/group) som passerat. */
  ids?: Record<string, string>;
  /** Diagnostik-flaggor — t.ex. "dropped-field:config.name". */
  redactedFields?: string[];
}

/** Allowlist av fält som får passera som numerisk skalär. */
const ALLOWED_SCALARS = new Set([
  'x', 'y', 'dx', 'dy', 'count', 'index', 'size',
  'width', 'height', 'zoom', 'sheetIndex',
  'fromX', 'fromY', 'toX', 'toY',
]);

/** Allowlist av fält som får passera som id (string). Måste vara id-format. */
const ALLOWED_ID_KEYS = new Set([
  'id', 'blockId', 'connectorId', 'groupId', 'layerId',
  'fromId', 'toId', 'parentGroupId', 'workflowId', 'sheetId',
  'targetId', 'sourceId',
]);

/** Fält som ALDRIG passerar oavsett typ — innehåller PII per definition. */
const FORBIDDEN_KEYS = new Set([
  'name', 'description', 'title', 'label', 'note', 'comment',
  'config', 'email', 'password', 'token', 'apiKey', 'secret',
  'content', 'body', 'message', 'prompt', 'response',
]);

/** Regex som känner igen typisk PII/secret-shape. */
const REDACT_PATTERNS: Array<{ re: RegExp; label: string }> = [
  { re: /\b[\w.+-]+@[\w-]+\.[\w.-]+\b/, label: 'email' },
  { re: /\beyJ[\w-]{8,}\.[\w-]{8,}\.[\w-]{8,}\b/, label: 'jwt' },
  { re: /\bsk-[\w]{20,}\b/, label: 'api-key' },
  { re: /\bAKIA[\w]{16}\b/, label: 'aws-key' },
  { re: /[?&][\w]+=[\w-]+/, label: 'url-param' },
  { re: /\b[0-9a-f]{32,}\b/i, label: 'long-hash' },
];

/** Id-shape: UUID, 'b123', 'g5', 'sheet-1', etc. */
const ID_SHAPE = /^[a-z0-9_-]{1,64}$/i;

function looksLikeId(v: unknown): v is string {
  return typeof v === 'string' && ID_SHAPE.test(v) && v.length <= 64;
}

function isRedactableString(s: string): string | null {
  for (const { re, label } of REDACT_PATTERNS) {
    if (re.test(s)) return label;
  }
  return null;
}

/**
 * Redacta ett action-event. Okända fält droppas. PII-mönster strippas.
 * Okända `kind` droppas helt (returnerar null).
 */
export function redactEvent(raw: RawActionEvent): RedactedActionEntry | null {
  if (!isActionKind(raw.kind)) return null;

  const redactedFields: string[] = [];
  const deltaScalars: Record<string, number> = {};
  const ids: Record<string, string> = {};

  const payload = raw.payload ?? {};
  for (const [key, value] of Object.entries(payload)) {
    if (FORBIDDEN_KEYS.has(key)) {
      redactedFields.push(`forbidden:${key}`);
      continue;
    }

    if (ALLOWED_SCALARS.has(key)) {
      if (typeof value === 'number' && Number.isFinite(value)) {
        deltaScalars[key] = value;
      } else {
        redactedFields.push(`not-number:${key}`);
      }
      continue;
    }

    if (ALLOWED_ID_KEYS.has(key)) {
      if (looksLikeId(value)) {
        ids[key] = value;
      } else if (typeof value === 'string') {
        const hit = isRedactableString(value);
        redactedFields.push(hit ? `pii:${key}:${hit}` : `bad-id-shape:${key}`);
      } else {
        redactedFields.push(`not-string-id:${key}`);
      }
      continue;
    }

    // Unknown key → drop + log
    redactedFields.push(`unknown:${key}`);
  }

  let target = raw.target;
  if (target !== undefined) {
    if (typeof target !== 'string' || target.length > 128) {
      redactedFields.push('target:too-long-or-wrong-type');
      target = undefined;
    } else {
      const pii = isRedactableString(target);
      if (pii) {
        redactedFields.push(`target:pii:${pii}`);
        target = undefined;
      }
    }
  }

  return {
    kind: raw.kind as ActionKind,
    ...(target !== undefined && { target }),
    ...(Object.keys(deltaScalars).length > 0 && { deltaScalars }),
    ...(Object.keys(ids).length > 0 && { ids }),
    ...(redactedFields.length > 0 && { redactedFields }),
  };
}

/** Batch-redact för utåt-gående snapshots. */
export function redactBatch(events: RawActionEvent[]): RedactedActionEntry[] {
  const out: RedactedActionEntry[] = [];
  for (const e of events) {
    const r = redactEvent(e);
    if (r) out.push(r);
  }
  return out;
}

/** Estimera byte-storleken på en redacted-entry (för size-capping). */
export function estimateBytes(entry: RedactedActionEntry): number {
  return JSON.stringify(entry).length;
}
